import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FaTrophy, FaPlus, FaChartBar, FaUsers, FaMedal, FaStar, FaShield, FaHandshake, FaHeart } from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { useNotification } from '../context/NotificationContext';
import { useUser } from '../context/UserContext';
import { canManageProgram } from '../hooks/useProgramsApi';
import styles from './AwardsPage.module.css';
import awardsAPI from '../api/awards';
import AwardAssignmentModal from '../components/Awards/AwardAssignmentModal';
import AwardEditModal from '../components/Awards/AwardEditModal';
import AwardCard from '../components/Awards/AwardCard';
import AwardStatistics from '../components/Awards/AwardStatistics';
import AwardLeaderboard from '../components/Awards/AwardLeaderboard';

const AwardsPage = () => {
  const { programId } = useParams();
  const { showSuccess, showError, showInfo } = useNotification();
  const { userClubs } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [program, setProgram] = useState(null);
  const [awards, setAwards] = useState([]);
  const [allAwards, setAllAwards] = useState([]); // For filtering
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAward, setSelectedAward] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsAward, setDetailsAward] = useState(null);
  
  // Permission state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [resolvedClubId, setResolvedClubId] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  
  // Authorization logic - same as Equipment/Teams page
  const canManage = useMemo(() => {
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    const hasProgramPermissions = canManageProgram(programPermissions);
    return hasClubRole || hasProgramPermissions;
  }, [userClubs, resolvedClubId, programPermissions]);

  // Fetch program permissions
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          setProgramPermissions([]);
        }
      } catch (error) {
        setProgramPermissions([]);
      }
    };

    fetchProgramPermissions();
  }, [programId]);
  
  // Fetch program details and awards data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch program details
        const programResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`);
        const programData = await programResponse.json();
        setProgram(programData);
        
        // Extract clubId for permissions
        const extractedClubId = programData.clubId && typeof programData.clubId === 'object'
          ? programData.clubId._id
          : programData.clubId;
        setResolvedClubId(extractedClubId);

        // Fetch program awards
        const awardsData = await awardsAPI.getProgramAwards(programId, { limit: 50 });
        setAwards(awardsData.awards);
        setAllAwards(awardsData.awards); // Store for filtering

        // Fetch program statistics (leaderboard)
        const statsData = await awardsAPI.getProgramStatistics(programId);
        setStatistics(statsData);

      } catch (error) {
        console.error('Error fetching awards data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (programId) {
      fetchData();
    }
  }, [programId]);

  const handleAwardCreated = async () => {
    // Refresh awards and statistics after creating a new award
    try {
      const awardsData = await awardsAPI.getProgramAwards(programId, { limit: 50 });
      setAwards(awardsData.awards);
      
      const statsData = await awardsAPI.getProgramStatistics(programId);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const getAwardIcon = (awardType) => {
    switch (awardType) {
      case 'individual': return <FaTrophy />;
      case 'team': return <FaUsers />;
      case 'position': return <FaMedal />;
      case 'statistical': return <FaChartBar />;
      case 'character': return <FaHandshake />;
      default: return <FaStar />;
    }
  };

  const getAwardColor = (awardType) => {
    switch (awardType) {
      case 'individual': return '#FFD700';
      case 'team': return '#C0C0C0';
      case 'position': return '#CD7F32';
      case 'statistical': return '#FF8C00';
      case 'character': return '#90EE90';
      default: return '#800080';
    }
  };

  const handleEditAward = (award) => {
    setSelectedAward(award);
    setShowEditModal(true);
  };

  const handleAwardUpdated = async () => {
    try {
      const awardsData = await awardsAPI.getProgramAwards(programId, { limit: 50 });
      setAwards(awardsData.awards);
      
      const statsData = await awardsAPI.getProgramStatistics(programId);
      setStatistics(statsData);
      
      setShowEditModal(false);
      setSelectedAward(null);
      showSuccess('Award updated successfully!');
    } catch (error) {
      console.error('Error refreshing after update:', error);
    }
  };

  const handleDeleteAward = async (awardId) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        showError('You must be logged in to delete awards.');
        return;
      }
      const token = await auth.currentUser.getIdToken();
      await awardsAPI.deleteAward(awardId, token);
      
      // Refresh the awards list
      const awardsData = await awardsAPI.getProgramAwards(programId, { limit: 50 });
      setAwards(awardsData.awards);
      setAllAwards(awardsData.awards);
      
      const statsData = await awardsAPI.getProgramStatistics(programId);
      setStatistics(statsData);
      
      showSuccess('Award deleted successfully!');
    } catch (error) {
      console.error('Error deleting award:', error);
      showError('Failed to delete award. Please try again.');
    }
  };
  
  const handleCardClick = (award) => {
    setDetailsAward(award);
    setShowDetailsModal(true);
  };
  
  // Apply filters - Filter awards based on search and filter criteria
  useEffect(() => {
    if (!allAwards || allAwards.length === 0) {
      setAwards([]);
      return;
    }
    
    let filtered = [...allAwards];
    
    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(award => 
        award.awardTemplateId?.awardName?.toLowerCase().includes(search) ||
        award.recipients?.some(r => 
          r.playerName?.toLowerCase().includes(search)
        ) ||
        award.eventId?.title?.toLowerCase().includes(search)
      );
    }
    
    // Type filter
    if (typeFilter && typeFilter !== 'all') {
      filtered = filtered.filter(award => 
        award.awardTemplateId?.awardType?.toLowerCase() === typeFilter.toLowerCase()
      );
    }
    
    // Date filter
    if (dateFilter && dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(award => {
        const awardDate = new Date(award.dateAwarded || award.awardedAt || award.createdAt);
        if (dateFilter === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return awardDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return awardDate >= monthAgo;
        } else if (dateFilter === 'season') {
          const threeMonthsAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          return awardDate >= threeMonthsAgo;
        }
        return true;
      });
    }
    
    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(b.dateAwarded || b.awardedAt || b.createdAt);
        const dateB = new Date(a.dateAwarded || a.awardedAt || a.createdAt);
        return dateA - dateB;
      });
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.dateAwarded || a.awardedAt || a.createdAt);
        const dateB = new Date(b.dateAwarded || b.awardedAt || b.createdAt);
        return dateA - dateB;
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => 
        (a.awardTemplateId?.awardName || '').localeCompare(b.awardTemplateId?.awardName || '')
      );
    } else if (sortBy === 'player') {
      filtered.sort((a, b) => {
        const nameA = a.recipients[0]?.playerName || '';
        const nameB = b.recipients[0]?.playerName || '';
        return nameA.localeCompare(nameB);
      });
    }
    
    setAwards(filtered);
  }, [allAwards, searchTerm, typeFilter, dateFilter, sortBy]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.compactHeader}>
            <div className={styles.headerLeft}>
              <FaTrophy className={styles.headerIcon} />
              <h1 className={styles.headerTitle}>Awards</h1>
              <span className={styles.itemCount}>Loading...</span>
            </div>
          </div>
          <div className={styles.loading}>
            <FaTrophy className={styles.loadingIcon} />
            <p>Loading awards...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Failed to load program details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Compact Sticky Header with Tabs */}
        <div className={styles.compactHeader}>
          {/* Left Section - Title */}
          <div className={styles.headerLeft}>
            <FaTrophy className={styles.headerIcon} />
            <h1 className={styles.headerTitle}>Awards</h1>
            <span className={styles.itemCount}>{allAwards.length} awards</span>
            {program && program.sport && (
              <span className={styles.sportBadge}>{program.sport}</span>
            )}
          </div>
          
          {/* Center Section - Tabs */}
          <div className={styles.compactTabs}>
            <button
              className={`${styles.compactTabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <span className={styles.tabIcon}>🏆</span>
              <span className={styles.tabText}>Awards</span>
              <span className={styles.tabCount}>({awards.length})</span>
            </button>
            <button
              className={`${styles.compactTabButton} ${activeTab === 'leaderboard' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('leaderboard')}
            >
              <span className={styles.tabIcon}>📊</span>
              <span className={styles.tabText}>Leaderboard</span>
              <span className={styles.tabCount}>({statistics.length})</span>
            </button>
          </div>
          
          {/* Right Section - Quick Stats */}
          <div className={styles.headerStats}>
            <span className={`${styles.statPill} ${styles.accent}`}>🏆 {allAwards.length} Total</span>
            <span className={styles.statPill}>👥 {statistics.length} players</span>
          </div>
        </div>

        {/* Floating Action Button */}
        {canManage && (
          <button
            className={styles.fabButton}
            onClick={() => setShowAssignmentModal(true)}
            title="Assign Award"
          >
            <FaPlus />
          </button>
        )}

        {/* Tab Content */}
        <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            {/* Controls Bar - Inline Stats + Filters */}
            <div className={styles.controlsBar}>
              {/* Inline Stats */}
              <div className={styles.inlineStats}>
                <span className={`${styles.inlineStat} ${styles.accent}`}>🏆 {allAwards.length} Total</span>
                <span className={styles.inlineStat}>👥 {statistics.length} Recipients</span>
              </div>
              
              {/* Search & Filters Row */}
              <div className={styles.filtersRow}>
                <input
                  type="text"
                  placeholder="Search awards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={styles.filterSelect}>
                  <option value="all">All Types</option>
                  <option value="individual">🏆 Individual</option>
                  <option value="statistical">📊 Statistical</option>
                  <option value="character">🤝 Character</option>
                  <option value="performance">⭐ Performance</option>
                  <option value="team">👥 Team</option>
                </select>
                
                <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className={styles.filterSelect}>
                  <option value="all">All Time</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="season">This Season</option>
                </select>
                
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={styles.filterSelect}>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="name">Name</option>
                  <option value="player">Player</option>
                </select>
              </div>
            </div>

            {/* Awards Grid */}
            <div className={styles.recentAwards}>
              <h2>Awards ({awards.length})</h2>
              <div className={styles.awardsGrid}>
                {awards.map((award) => (
                  <AwardCard 
                    key={award._id} 
                    award={award} 
                    onEdit={canManage ? handleEditAward : null}
                    onDelete={canManage ? handleDeleteAward : null}
                    onClick={() => handleCardClick(award)}
                    compact={true}
                  />
                ))}
              </div>
              {awards.length === 0 && (
                <div className={styles.emptyState}>
                  <FaTrophy className={styles.emptyIcon} />
                  <p>No awards have been given yet.</p>
                  <button
                    className={styles.emptyButton}
                    onClick={() => setShowAssignmentModal(true)}
                  >
                    Assign First Award
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className={styles.leaderboardTab}>
            <AwardLeaderboard statistics={statistics} />
          </div>
        )}
      </div>

      {/* Award Assignment Modal */}
      {showAssignmentModal && (
        <AwardAssignmentModal
          programId={programId}
          programSport={program.sport}
          onClose={() => setShowAssignmentModal(false)}
          onAwardCreated={handleAwardCreated}
          selectedEvent={selectedEvent}
        />
      )}

      {/* Edit Award Modal */}
      {showEditModal && selectedAward && (
        <AwardEditModal
          award={selectedAward}
          programId={programId}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAward(null);
          }}
          onAwardUpdated={handleAwardUpdated}
        />
      )}
      
      {/* Award Details Modal */}
      {showDetailsModal && detailsAward && (
        <div className={styles.modalOverlay} onClick={() => setShowDetailsModal(false)}>
          <div className={styles.detailsModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Award Details</h2>
              <button onClick={() => setShowDetailsModal(false)} className={styles.closeBtn}>
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <AwardCard 
                award={detailsAward} 
                showFullDetails={true}
                onEdit={canManage ? handleEditAward : null}
                onDelete={canManage ? ((id) => {
                  handleDeleteAward(id);
                  setShowDetailsModal(false);
                }) : null}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AwardsPage;
